import { BenefitClaimModal } from '@/components/modal/BenefitClaimModal'
import { useCanUseBenefits } from '@/hooks/useCanUseBenefits'
import { CLUB_TARGET_KIND, HeldBenefit, clubTargetUrn, useClaimableAtClub } from '@/services/myBenefits'
import { claimableAt } from '@/pages/benefits/claimOptions'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  clubId: number
  clubName: string
}

/**
 * "You can claim your benefit here" on a club page.
 *
 * Renders nothing unless the server says this member can actually claim at this club. That single
 * call answers participation, eligibility and whether they hold anything at all — and deliberately
 * does not distinguish between them, so a member who fails a club's eligibility rule never learns
 * the rule exists.
 *
 * The club is fixed, so the modal opens straight to confirmation rather than showing a picker the
 * member has already made their choice in.
 */
export const ClubBenefitClaimBanner = ({ clubId, clubName }: Props) => {
  const { t } = useTranslation()
  const benefitsEnabled = useCanUseBenefits()
  const { data: claimable } = useClaimableAtClub(clubId, benefitsEnabled)
  const [claiming, setClaiming] = useState<HeldBenefit | null>(null)
  const [claimed, setClaimed] = useState(false)

  // What the server says can be claimed here, narrowed to a benefit (or a bundle's part) that names a
  // club — never one that names somewhere else.
  const benefit = claimableAt(claimable ?? [], CLUB_TARGET_KIND)
  if (benefit === null || claimed) return null

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white/10 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-white">{t('benefits.claimAtClub')}</p>
          <p className="text-xs text-white/70">{benefit.label ?? t('benefits.aBenefit')}</p>
        </div>
        <button
          className="rounded bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-opacity hover:opacity-90"
          onClick={() => setClaiming(benefit)}
        >
          {t('benefits.claimCta')}
        </button>
      </div>

      <BenefitClaimModal
        open={claiming !== null}
        onClose={() => setClaiming(null)}
        benefit={claiming}
        fixedTarget={{ kind: 'club', id: clubId, name: clubName, urn: clubTargetUrn(clubId) }}
        // The query refetch will empty `claimable` anyway; this just stops the banner flashing back
        // in for the moment between the claim landing and the refetch resolving.
        onClaimed={() => setClaimed(true)}
        // Uncontrolled: with a fixed target there is no selection to lift out of the modal.
        selectedTargetUrn={null}
        onSelectTarget={null}
      />
    </>
  )
}
